import { Button, Checkbox, Input, Menu, Spinner, Table } from '@cma/common'
import { DocumentIcon } from '@cma/icons'
import {
  DropdownButton,
  DropdownButtonItem,
  DropdownButtonMenu
} from '@helix/dropdown-button'
import { HelixIcon } from '@helix/helix-icon'
import {
  cards as CardsIcon,
  chevron_small_down as ChevronDownIcon,
  chevron_left as ChevronLeftIcon,
  list as ListIcon,
  plus as PlusIcon,
  plus_small as PlusSmallIcon,
  search as SearchIcon
} from '@helix/helix-icon/outlined'
import React, {
  ChangeEvent,
  Suspense,
  useCallback,
  useEffect,
  useRef,
  useState
} from 'react'
import { CheckedContentFloatingActionBar } from './CheckedContentFloatingActionBar'
import { ContentCard, UploadCard } from './ContentCard'
import { ContentProvider, useContent } from './ContentContext'
import { ContentFileUploader } from './ContentFileUploader'
import { ContentPreviewModal } from './ContentPreviewModal'
import { ContentTableRow, ContentTableRowSkeleton } from './ContentTableRow'
import { DragContentProvider } from './DragContentContext'
import {
  GridFolderCreateInput,
  TableFolderCreateInput
} from './FolderCreateInput'
import { ShareContentModal } from './share_modal/ShareContentModal'
import { FileSortEnum } from './useFileQuery'
import { useViewMode } from './useViewMode'

export default function AdminContent() {
  return (
    <Suspense fallback={<AdminContentSkeleton />}>
      <ContentProvider>
        <DragContentProvider>
          <AdminContent_ />
        </DragContentProvider>
      </ContentProvider>
    </Suspense>
  )
}

function AdminContent_() {
  const {
    uploadRef,
    currentFolder,
    contents,
    filteredContents,
    isAllSelected,
    search,
    sortKey,
    viewMode,
    createNewFolder,
    hasContentPermissions,
    previewContent,
    setPreviewContent,
    toggleViewMode,
    setSearch,
    navigateToFolder,
    selectAllFiles,
    deselectAllFiles,
    setSortKey
  } = useContent()

  const [isShare, setIsShare] = useState(true)
  const [showSharingModal, setShowSharingModal] = useState(false)
  const [isUploading, setIsUploading] = useState(false)
  const [totalFilesUploading, setTotalFilesUploading] = useState(0)
  const gridScrollRef = useRef<HTMLDivElement>(null)
  const tableScrollRef = useRef<HTMLDivElement>(null)

  const smoothScrollTo = useCallback(
    (container: HTMLDivElement, targetTop: number) => {
      container.scrollTo({
        top: targetTop,
        behavior: 'smooth'
      })
    },
    []
  )

  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      const scrollContainer =
        viewMode === 'grid' ? gridScrollRef.current : tableScrollRef.current

      if (!scrollContainer) return

      const scrollAmount = 100
      const currentScrollTop = scrollContainer.scrollTop
      const { clientHeight, scrollHeight } = scrollContainer

      const scrollActions: Record<string, () => void> = {
        ArrowDown: () =>
          smoothScrollTo(scrollContainer, currentScrollTop + scrollAmount),
        ArrowUp: () =>
          smoothScrollTo(scrollContainer, currentScrollTop - scrollAmount),
        PageDown: () =>
          smoothScrollTo(
            scrollContainer,
            currentScrollTop + clientHeight * 0.8
          ),
        PageUp: () =>
          smoothScrollTo(
            scrollContainer,
            currentScrollTop - clientHeight * 0.8
          ),
        Home: () =>
          (event.ctrlKey || event.metaKey) &&
          smoothScrollTo(scrollContainer, 0),
        End: () =>
          (event.ctrlKey || event.metaKey) &&
          smoothScrollTo(scrollContainer, scrollHeight)
      }

      const action = scrollActions[event.key]
      if (action) {
        event.preventDefault()
        action()
      }
    },
    [viewMode, smoothScrollTo]
  )

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown)
    return () => {
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [handleKeyDown])

  if (!contents?.length && !currentFolder) return <AdminContentEmptyState />

  const availableSortOptions = Object.values(FileSortEnum).filter((option) => {
    return currentFolder ? option !== FileSortEnum.Folders : true
  })

  return (
    <>
      <div className="flex max-h-[calc(100vh-280px)] min-h-[338px] flex-1 flex-col">
        {currentFolder && (
          <div className="helix-d-inline-flex helix-align--middle mb-2">
            <button
              className="helix-btn helix-btn--small helix-d-inline-flex helix-align--middle helix-btn--ghost"
              onClick={() => navigateToFolder(null)}>
              <React.Fragment key=".0">
                <HelixIcon className="helix-mr-2" icon={ChevronLeftIcon} />
                <span>All Content</span>
              </React.Fragment>
            </button>
          </div>
        )}
        <div className="mb-5 flex items-center justify-between">
          <h2 className="helix-display-small">
            {currentFolder?.displayName || 'Content'}
          </h2>
          {!currentFolder && (
            <div className="w-full max-w-[33%]">
              <Input
                size="xxs"
                aria-label="search"
                leftIcon={<HelixIcon icon={SearchIcon} />}
                placeholder="Search"
                value={search}
                onChange={(e: ChangeEvent<HTMLInputElement>) =>
                  setSearch(e.target.value)
                }
              />
            </div>
          )}
          {!currentFolder ? (
            <Button
              disabled={!hasContentPermissions}
              loading={isUploading}
              onClick={() =>
                hasContentPermissions && uploadRef.current?.click()
              }>
              {isUploading ? 'Uploading' : 'Upload'}
            </Button>
          ) : (
            <>
              <DropdownButton
                color="primary"
                disabled={isUploading || !hasContentPermissions}
                label={
                  isShare
                    ? isUploading
                      ? 'Uploading'
                      : 'Share Folder'
                    : isUploading
                    ? 'Uploading'
                    : 'Upload'
                }
                onClick={() => {
                  if (!hasContentPermissions) return
                  if (isShare) setShowSharingModal(true)
                  else uploadRef.current?.click()
                }}>
                <DropdownButtonMenu>
                  <DropdownButtonItem
                    id="preview-delete-button"
                    textLabel={isShare ? 'Delete Brand' : 'View Preview'}
                    className="w-[110px] cursor-pointer hover:!bg-gray-100"
                    onClick={() =>
                      hasContentPermissions && setIsShare(!isShare)
                    }>
                    <div className="flex items-center">
                      <span>{isShare ? 'Upload File' : 'Share Folder'}</span>
                    </div>
                  </DropdownButtonItem>
                </DropdownButtonMenu>
              </DropdownButton>
              <ShareContentModal
                ids={[{ id: currentFolder.id, type: 'Folder' }]}
                open={showSharingModal && hasContentPermissions}
                onClose={() => setShowSharingModal(false)}
              />
            </>
          )}
        </div>
        <div className="mb-5 flex items-center justify-between">
          <div className="flex items-center space-x-4">
            {viewMode !== 'table' && (
              <Checkbox
                checked={isAllSelected}
                onChange={() =>
                  isAllSelected ? deselectAllFiles() : selectAllFiles()
                }
              />
            )}
            <Menu
              button={
                <button className="helix-btn helix-btn--ghost helix-btn--small flex items-center">
                  <span className="helix-text--capitalize ml-2 mr-2 text-gray-700">
                    {sortKey}
                  </span>
                  <HelixIcon icon={ChevronDownIcon} />
                </button>
              }>
              {availableSortOptions.map((option) => (
                <Menu.Item
                  key={option}
                  as="button"
                  onClick={() => setSortKey(option)}>
                  <div className="flex items-center">
                    <span
                      className={`helix-text--capitalize ${
                        sortKey === option
                          ? 'font-medium text-brand-primary'
                          : 'text-gray-700'
                      }`}>
                      {option}
                    </span>
                  </div>
                </Menu.Item>
              ))}
            </Menu>
          </div>
          <button
            className="helix-btn helix-btn--small"
            onClick={toggleViewMode}>
            <HelixIcon
              icon={viewMode === 'grid' ? ListIcon : CardsIcon}
              className="h-5 w-5 text-gray-700"
            />
          </button>
        </div>
        {viewMode === 'grid' ? (
          <div
            ref={gridScrollRef}
            className="mx-auto flex-1 overflow-y-auto overflow-x-hidden pt-1"
            tabIndex={-1}>
            <div className="grid flex-1 grid-cols-2 gap-4 overflow-hidden px-1 pt-1 sm:grid-cols-3 md-lg:grid-cols-5">
              <UploadCard
                uploadRef={uploadRef}
                disabled={!hasContentPermissions}
              />
              {createNewFolder && hasContentPermissions && (
                <GridFolderCreateInput />
              )}
              {filteredContents?.map((item) => (
                <ContentCard
                  key={`${item.__typename}-${item.id}`}
                  item={item}
                />
              ))}
              {isUploading && totalFilesUploading && (
                <>
                  {Array.from({ length: totalFilesUploading }).map(
                    (_, index) => (
                      <div key={index} className="flex flex-col gap-2">
                        <div className="flex aspect-square flex-col items-center justify-center bg-gray-50 px-3 py-4">
                          <div className="h-[70px] w-[55px] animate-pulse rounded-md bg-gray-200" />
                        </div>
                        <div className="flex items-center gap-2">
                          <div className="h-3 w-2/3 animate-pulse rounded-sm bg-gray-200" />
                          <div className="h-3.5 w-3.5 text-gray-300">
                            <Spinner />
                          </div>
                        </div>
                      </div>
                    )
                  )}
                </>
              )}
            </div>
          </div>
        ) : (
          <div
            ref={tableScrollRef}
            id="content-table"
            className="flex-1 overflow-y-auto overflow-x-hidden rounded-lg bg-white"
            tabIndex={-1}>
            <Table style={{ overflow: 'hidden' }}>
              <Table.Head style={{ position: 'sticky', top: 0, zIndex: 10 }}>
                <Table.Row>
                  <Table.Header align="center">
                    <Checkbox
                      checked={isAllSelected}
                      onChange={() =>
                        isAllSelected ? deselectAllFiles() : selectAllFiles()
                      }
                    />
                  </Table.Header>
                  <Table.Header>Name</Table.Header>
                  <Table.Header>Shared With</Table.Header>
                  <Table.Header></Table.Header>
                  <Table.Header></Table.Header>
                </Table.Row>
              </Table.Head>
              <Table.Body>
                {!filteredContents.length && !!search && (
                  <Table.Row>
                    <Table.Data colSpan={5}>
                      <div className="flex justify-center">
                        <p className="helix-body px-4 py-2">
                          There are no results for{' '}
                          <span className="helix-body--strong">"{search}"</span>
                        </p>
                      </div>
                    </Table.Data>
                  </Table.Row>
                )}
                {isUploading && <ContentTableRowSkeleton />}
                {createNewFolder && hasContentPermissions && (
                  <TableFolderCreateInput />
                )}
                {filteredContents.map((item, index) => (
                  <ContentTableRow
                    key={`${item.__typename === 'Folder' ? 'folder' : 'file'}-${
                      item.id
                    }`}
                    index={index}
                    item={item}
                  />
                ))}
              </Table.Body>
            </Table>
          </div>
        )}
        <CheckedContentFloatingActionBar filteredContents={filteredContents} />
      </div>
      <ContentPreviewModal
        content={previewContent}
        onClose={() => setPreviewContent(null)}
      />
      {hasContentPermissions && (
        <ContentFileUploader
          inputRef={uploadRef}
          isUploading={(val) => setIsUploading(val)}
          onFilesSizeChange={(n) => setTotalFilesUploading(n)}
        />
      )}
    </>
  )
}

function AdminContentSkeleton() {
  const { viewMode } = useViewMode()

  return (
    <div className="flex max-h-[calc(100vh-280px)] min-h-[338px] flex-col">
      <div className="mb-5 flex items-center justify-between">
        <h2 className="helix-display-small">Content</h2>
        <div className="w-full max-w-[33%]">
          <Input
            disabled
            size="xxs"
            aria-label="search"
            leftIcon={<HelixIcon icon={SearchIcon} />}
            placeholder="Search"
          />
        </div>
        <Button disabled>Upload</Button>
      </div>
      <div className="mb-5 flex items-center justify-between">
        <button className="helix-btn helix-btn--ghost helix-btn--small flex items-center">
          <span className="helix-text--capitalize ml-2 mr-2 text-gray-700">
            All
          </span>
          <HelixIcon icon={ChevronDownIcon} />
        </button>
        <button disabled className="helix-btn helix-btn--small">
          <HelixIcon
            icon={viewMode === 'grid' ? ListIcon : CardsIcon}
            className="h-5 w-5 text-gray-700"
          />
        </button>
      </div>
      {viewMode === 'grid' ? (
        <div className="grid flex-1 grid-cols-2 gap-4 overflow-y-auto sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5">
          {Array.from({ length: 5 }).map((_, index) => (
            <div key={index} className="flex flex-col gap-2">
              <div className="flex aspect-square flex-col items-center justify-center bg-gray-50 px-3 py-4">
                <div className="h-[70px] w-[55px] animate-pulse rounded-md bg-gray-200" />
              </div>
              <div className="h-3 w-2/3 animate-pulse rounded-sm bg-gray-200" />
            </div>
          ))}
        </div>
      ) : (
        <div className="flex-1 overflow-y-auto rounded-lg bg-white text-sm font-normal text-gray-500 shadow">
          <Table>
            <Table.Head>
              <Table.Row>
                <Table.Header align="center">
                  <Checkbox />
                </Table.Header>
                <Table.Header>Name</Table.Header>
                <Table.Header>Shared With</Table.Header>
                <Table.Header></Table.Header>
                <Table.Header></Table.Header>
              </Table.Row>
            </Table.Head>
            <Table.Body>
              <Table.Row>
                <Table.Data colSpan={5}>
                  <div className="flex items-center space-x-2 text-gray-500">
                    <div className="h-5 w-5">
                      <Spinner />
                    </div>
                    <div>Loading Contents</div>
                  </div>
                </Table.Data>
              </Table.Row>
            </Table.Body>
          </Table>
        </div>
      )}
    </div>
  )
}

function AdminContentEmptyState() {
  const { uploadRef } = useContent()
  const [isUploading, setIsUploading] = useState(false)

  return (
    <>
      <div className="flex max-h-[calc(100vh-280px)] min-h-[338px] flex-col">
        <div className="flex items-center justify-between">
          <h2 className="helix-display-small">Content</h2>
          <Button
            loading={isUploading}
            leftIcon={!isUploading ? <HelixIcon icon={PlusIcon} /> : undefined}
            onClick={() => uploadRef.current?.click()}>
            {isUploading ? 'Uploading' : 'Add content'}
          </Button>
        </div>
        <div className="mx-auto flex flex-1 flex-col items-center justify-center md:max-w-[451px]">
          <div className="mb-[16px] flex h-[56px] w-[56px] items-center justify-center rounded-full bg-brand-white">
            <DocumentIcon className="text-brand-primary" />
          </div>
          <h2 className="helix-heading mb-1">Content</h2>
          <p className="helix-body text-center text-gray-500">
            Add and manage content within your organization.
            <br className="hidden md:block" /> Customize what custom content
            appears across Cloud CMA reports.
          </p>
          <div className="mt-6 flex items-center gap-2">
            <button className="helix-btn helix-btn--ghost">Learn more</button>
            <Button
              variant="tertiary"
              loading={isUploading}
              leftIcon={
                !isUploading ? <HelixIcon icon={PlusSmallIcon} /> : undefined
              }
              onClick={() => uploadRef.current?.click()}>
              {isUploading ? 'Uploading' : 'Add content'}
            </Button>
          </div>
        </div>
      </div>
      <ContentFileUploader
        inputRef={uploadRef}
        isUploading={(isUploading) => setIsUploading(isUploading)}
      />
    </>
  )
}
